import AppComponent from '../base/app-component.ts';
import { templateParseToElement } from '../lib/template-parse.ts';
import type { ColoreDelega, ModalitaRaccolta } from '@/types/entities/raccolta-delega.ts';
import { etichettaStato } from '@/types/entities/delega.ts';

// Interfacce locali minimali (compatibili con i tipi Zod restituiti dalle API,
// senza dipendere direttamente da z.infer/z.input che divergono con .default())
interface DelegaInfo {
  id: number;
  lavoratoreId: number;
  cassa: string;
  stato?: string;
  dataDecorrenza: string | null;
  operatore: string | null;
  operatoreNome?: string | null;
  note: string | null;
  coloreId?: number | null;
  modalitaRaccolta?: string | null;
}

interface LavoratoreInfo {
  id: number;
  cognome: string;
  nome: string;
  codiceFiscale: string | null;
  dataNascita: string | null;
  posizioni: {
    CE?: { impresaRagioneSociale: string | null } | undefined;
    EC?: { impresaRagioneSociale: string | null } | undefined;
  };
}

type ModalInstance = { show(): void; hide(): void; dispose(): void };

/**
 * wc-edit-delega — Inserimento e modifica delega sindacale.
 *
 * Attributi:
 *   delega-id    (opzionale) ID delega da modificare; assente = inserimento
 *
 * Modalita' inserimento (no delega-id):
 *   Step 1 → Ricerca lavoratore (debounce 300ms, min 3 char)
 *   Step 2 → Conferma lavoratore + warning deleghe attive
 *   Step 3 → Form dati delega
 *   Step 4 → Riepilogo e conferma → POST /api/deleghe
 *
 * Modalita' modifica (delega-id presente):
 *   Carica delega e lavoratore → salta a step 3 pre-compilato
 *   Submit → PUT /api/deleghe/:id
 *
 * Successo: postMessage al parent (iframe) per chiusura modale.
 */
export class EditDelega extends AppComponent {
  static override componentName = 'wc-edit-delega';

  private step = 1;
  private lavoratore: LavoratoreInfo | null = null;
  private delegheEsistenti: DelegaInfo[] = [];
  private delegaId = 0;
  private isSospesa = false;
  private fromDisdettaId = 0;
  private nascondiDelegheEsistenti = false;
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;
  private modals: ModalInstance[] = [];

  protected override get templateId() { return 'edit-delega'; }

  protected override afterRender(): void {
    this.delegaId = this.attributeNumber('delega-id');
    if (this.delegaId) {
      this.loadDelegaEsistente();
    } else {
      this.setupStep1();
    }

    this.appendModals();
    this.setupNuovoLavoratore();
    this.checkPreselezione();
  }

  // --- Preselezione lavoratore: salta step 1 (da disdetta o da dettaglio lavoratore) ---

  private async checkPreselezione(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    const fromDisdetta = Number(params.get('fromDisdetta'));
    const lavoratoreIdParam = Number(params.get('lavoratoreId'));
    const defaultCassa = params.get('defaultCassa');

    // Da scheda lavoratore: l'aggiornamento di una delega esistente si fa dal menu
    // contestuale, quindi qui non si mostra il box "Deleghe attive esistenti".
    this.nascondiDelegheEsistenti = params.get('noAggiorna') === '1';

    // Da disdetta: ricava il lavoratoreId dalla disdetta. Altrimenti usa lavoratoreId diretto.
    let lavoratoreId = 0;
    if (fromDisdetta > 0) {
      this.fromDisdettaId = fromDisdetta;
      const resDisdetta = await this.api.disdette.dettaglio(fromDisdetta);
      if (!resDisdetta.ok) return;
      lavoratoreId = resDisdetta.data.lavoratoreId;
    } else if (lavoratoreIdParam > 0) {
      lavoratoreId = lavoratoreIdParam;
    } else {
      return;
    }

    const resLav = await this.api.lavoratori.dettaglio(lavoratoreId);
    if (!resLav.ok) return;

    // Salta step 1 (ricerca) e va direttamente al lavoratore selezionato
    this.lavoratore = resLav.data;
    this.goToStep2();

    // Pre-seleziona la cassa se indicata
    if (defaultCassa) {
      this.setCassa(defaultCassa);
    }
  }

  // --- Helper operatore (dal combobox) ---

  private getOperatoreCombobox() {
    return this.$('wc-operatore-combobox') as HTMLElement & {
      value: number | null; operatoreNome: string | null; clearSelection(): void; preseleziona(idFenice: number, label: string): void;
    } | null;
  }

  // --- Modalita' modifica: carica dati esistenti ---

  private async loadDelegaEsistente(): Promise<void> {
    const container = this.$('[data-step="1"]');
    if (container) {
      container.classList.remove('d-none');
      templateParseToElement('shared-loading-state', container, {});
    }

    const delegaResult = await this.api.deleghe.dettaglio(this.delegaId);
    if (!delegaResult.ok) {
      if (container) templateParseToElement('shared-error-state', container, { message: delegaResult.error });
      return;
    }

    const delega = delegaResult.data;

    const lavResult = await this.api.lavoratori.dettaglio(delega.lavoratoreId);
    if (!lavResult.ok) {
      if (container) templateParseToElement('shared-error-state', container, { message: lavResult.error });
      return;
    }

    this.lavoratore = lavResult.data;

    const delegheLav = await this.api.lavoratori.deleghe(delega.lavoratoreId);
    if (delegheLav.ok) {
      this.delegheEsistenti = delegheLav.data.deleghe.filter(
        d => d.id !== this.delegaId && d.stato !== 'ARCHIVIATA'
      );
    }

    this.goToStep3WithData(delega);
  }

  // --- Step 1: Ricerca lavoratore ---

  private setupStep1(): void {
    const input = this.$('[data-search-input]') as HTMLInputElement | null;
    if (!input) return;

    this.listen(input, 'input', () => {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      const q = input.value.trim();
      if (q.length < 3) {
        const results = this.$('[data-search-results]');
        if (results) results.innerHTML = '';
        return;
      }
      this.searchTimeout = setTimeout(() => this.doSearch(q), 300);
    });
  }

  private async doSearch(q: string): Promise<void> {
    const container = this.$('[data-search-results]');
    if (!container) return;

    templateParseToElement('shared-loading-state', container, {});

    const result = await this.api.lavoratori.cerca(q);
    if (!result.ok) {
      templateParseToElement('shared-error-state', container, { message: result.error });
      return;
    }

    const lavoratori = result.data.risultati;
    templateParseToElement('shared-lavoratori-risultati', container, { lavoratori, action: 'seleziona-lavoratore' });

    this.delegate.offAll('click', '[data-action="seleziona-lavoratore"]');
    this.delegate.on('click', '[data-action="seleziona-lavoratore"]', (e: Event) => {
      const btn = (e.target as HTMLElement).closest('[data-action="seleziona-lavoratore"]') as HTMLElement;
      const id = Number(btn.dataset.id);
      const lav = lavoratori.find(l => l.id === id);
      if (lav) {
        this.lavoratore = lav;
        this.goToStep2();
      }
    });
  }

  // --- Step 2: Conferma lavoratore ---

  private async goToStep2(): Promise<void> {
    this.showStep(2);

    if (!this.lavoratore) return;

    const infoContainer = this.$('[data-lavoratore-info]');
    if (infoContainer) {
      templateParseToElement('edit-delega-lavoratore-info', infoContainer, { lavoratore: this.lavoratore });
    }

    await this.reloadDelegheEsistenti();

    this.delegate.offAll('click', '[data-action="modifica-delega-esistente"]');
    this.delegate.on('click', '[data-action="modifica-delega-esistente"]', async (e: Event) => {
      const btn = (e.target as HTMLElement).closest('[data-action="modifica-delega-esistente"]') as HTMLElement | null;
      if (!btn) return;
      const id = Number(btn.dataset.id);
      if (!id) return;
      const result = await (globalThis as any).Weext.modalIframe(`/deleghe/${id}/modifica?layout=modal`, {
        title: 'Aggiorna delega esistente',
        size: 'lg',
      });
      if (result?.refresh) await this.reloadDelegheEsistenti();
    });

    this.delegate.offAll('click', '[data-action="conferma-lavoratore"]');
    this.delegate.on('click', '[data-action="conferma-lavoratore"]', () => this.goToStep3());

    this.delegate.offAll('click', '[data-action="cambia-lavoratore"]');
    this.delegate.on('click', '[data-action="cambia-lavoratore"]', () => {
      this.lavoratore = null;
      this.delegheEsistenti = [];
      this.showStep(1);
      const input = this.$('[data-search-input]') as HTMLInputElement | null;
      if (input) { input.value = ''; input.focus(); }
      const results = this.$('[data-search-results]');
      if (results) results.innerHTML = '';
    });
  }

  private async reloadDelegheEsistenti(): Promise<void> {
    if (!this.lavoratore) return;
    const delegheResult = await this.api.lavoratori.deleghe(this.lavoratore.id);
    const warningContainer = this.$('[data-deleghe-warning]');
    if (!delegheResult.ok || !warningContainer) return;
    this.delegheEsistenti = delegheResult.data.deleghe.filter(d => d.stato !== 'ARCHIVIATA');
    // delegheEsistenti serve comunque al warning anti-duplicato dello step 3,
    // ma il box "Aggiorna" si mostra solo fuori dal contesto scheda lavoratore.
    if (this.delegheEsistenti.length > 0 && !this.nascondiDelegheEsistenti) {
      templateParseToElement('edit-delega-warning', warningContainer, { deleghe: this.delegheEsistenti });
    } else {
      warningContainer.innerHTML = '';
    }
  }

  // --- Step 3: Form delega ---

  private async goToStep3(): Promise<void> {
    this.showStep(3);

    // Reset checkbox sospesa
    this.isSospesa = false;
    const checkSospesa = this.$('[data-field="sospesa"]') as HTMLInputElement | null;
    if (checkSospesa) checkSospesa.checked = false;
    const gruppoCassa = this.$('[data-gruppo-cassa]');
    if (gruppoCassa) gruppoCassa.classList.remove('d-none');

    this.setupStep3Listeners();

    // Mostra banner lavoratore
    const banner = this.$('[data-lavoratore-banner]');
    if (banner && this.lavoratore) {
      banner.classList.remove('d-none');
      banner.innerHTML = `<strong>${this.lavoratore.cognome} ${this.lavoratore.nome}</strong>` +
        (this.lavoratore.codiceFiscale ? ` <code class="ms-2">${this.lavoratore.codiceFiscale}</code>` : '');
    }
  }

  private async goToStep3WithData(delega: DelegaInfo): Promise<void> {
    this.showStep(3);
    // Mostra banner lavoratore
    const banner = this.$('[data-lavoratore-banner]');
    if (banner && this.lavoratore) {
      banner.classList.remove('d-none');
      banner.innerHTML = `<strong>${this.lavoratore.cognome} ${this.lavoratore.nome}</strong>` +
        (this.lavoratore.codiceFiscale ? ` <code class="ms-2">${this.lavoratore.codiceFiscale}</code>` : '');
    }

    // Pre-compila campi
    this.setCassa(delega.cassa);

    const decField = this.$('[data-field="dataDecorrenza"]') as HTMLInputElement | null;
    if (decField && delega.dataDecorrenza) decField.value = delega.dataDecorrenza;

    // Pre-compila operatore nel combobox
    if (delega.operatore) {
      const combo = this.getOperatoreCombobox();
      const opNome = delega.operatoreNome;
      const idFenice = Number(delega.operatore);
      if (combo && opNome && idFenice) combo.preseleziona(idFenice, opNome);
    }

    const noteField = this.$('[data-field="note"]') as HTMLTextAreaElement | null;
    if (noteField && delega.note) noteField.value = delega.note;

    this.setColoreModalita(delega.coloreId ?? null, delega.modalitaRaccolta ?? null);

    this.setupStep3Listeners();

    // Nascondi tasto "Indietro" (non si torna a step 2 in modifica)
    const backBtn = this.$('[data-action="torna-step2"]');
    if (backBtn) backBtn.classList.add('d-none');

    // Nascondi checkbox sospesa in modalità modifica
    const wrapperSospesa = this.$('[data-wrapper-sospesa]');
    if (wrapperSospesa) wrapperSospesa.classList.add('d-none');
  }

  private setupStep3Listeners(): void {
    // Warning cassa duplicata
    const cassaContainer = this.$('[data-field="cassa"]');
    if (cassaContainer) {
      this.listen(cassaContainer, 'change', () => {
        const warning = this.$('[data-cassa-warning]');
        const avantiBtn = this.$('[data-action="vai-riepilogo"]') as HTMLButtonElement | null;
        if (!warning) return;
        const cassa = this.getCassa();
        const dup = this.delegheEsistenti.find(d => d.cassa === cassa);
        if (dup) {
          warning.textContent = `Attenzione: esiste già una delega ${dup.cassa} (${etichettaStato(dup.stato ?? '')})`;
          warning.classList.remove('d-none');
          if (avantiBtn) avantiBtn.disabled = true;
        } else {
          warning.classList.add('d-none');
          if (avantiBtn) avantiBtn.disabled = false;
        }
      });
    }


    // Checkbox "Salva in archivio"
    this.delegate.on('change', '[data-field="sospesa"]', (e: Event) => {
      const checked = (e.target as HTMLInputElement).checked;
      this.isSospesa = checked;
      const gruppoCassa = this.$('[data-gruppo-cassa]');
      if (gruppoCassa) gruppoCassa.classList.toggle('d-none', checked);
      const btnAvanti = this.$('[data-action="vai-riepilogo"]');
      if (btnAvanti) btnAvanti.textContent = checked ? 'Salva in archivio' : 'Avanti';
    });

    this.delegate.offAll('click', '[data-action="vai-riepilogo"]');
    this.delegate.on('click', '[data-action="vai-riepilogo"]', () => this.goToStep4());

    this.delegate.offAll('click', '[data-action="torna-step2"]');
    this.delegate.on('click', '[data-action="torna-step2"]', () => this.goToStep2());
  }

  // --- Step 4: Riepilogo e conferma ---

  private goToStep4(): void {
    if (this.isSospesa) { this.submitArchivio(); return; }
    const cassa = this.getCassa();
    if (!cassa) {
      const container = this.$('[data-field="cassa"]');
      if (container) container.classList.add('is-invalid');
      return;
    }

    const combo = this.getOperatoreCombobox();
    if (!combo?.value) {
      Weext.toast('Seleziona un operatore', { type: 'warning' });
      return;
    }
    if (!this.delegaId && !this.verificaColoreModalita()) return;

    this.showStep(4);

    const dataDecorrenza = (this.$('[data-field="dataDecorrenza"]') as HTMLInputElement | null)?.value || null;
    const operatore = this.getOperatoreCombobox()?.operatoreNome ?? null;
    const note = (this.$('[data-field="note"]') as HTMLTextAreaElement | null)?.value || null;
    const colore = this.colori.find(c => c.id === this.getColoreId());

    const riepilogoContainer = this.$('[data-riepilogo]');
    if (riepilogoContainer && this.lavoratore) {
      templateParseToElement('edit-delega-riepilogo', riepilogoContainer, {
        lavoratore: this.lavoratore,
        cassa,
        dataDecorrenza,
        operatore,
        note,
        coloreNome: colore?.nome ?? null,
        coloreCss: colore?.css ?? null,
        modalitaRaccolta: this.getModalitaRaccolta(),
        isModifica: this.delegaId > 0,
      });
    }

    this.delegate.offAll('click', '[data-action="conferma-inserimento"]');
    this.delegate.on('click', '[data-action="conferma-inserimento"]', () => this.submit());

    this.delegate.offAll('click', '[data-action="torna-step3"]');
    this.delegate.on('click', '[data-action="torna-step3"]', () => this.showStep(3));
  }

  // --- Submit ---

  private async submit(): Promise<void> {
    const erroreContainer = this.$('[data-errore]');
    if (erroreContainer) erroreContainer.innerHTML = '';

    const confirmBtn = this.$('[data-action="conferma-inserimento"]') as HTMLButtonElement | null;
    if (confirmBtn) {
      confirmBtn.disabled = true;
      confirmBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Salvataggio...';
    }

    const cassa = this.getCassa();
    const dataDecorrenza = (this.$('[data-field="dataDecorrenza"]') as HTMLInputElement | null)?.value || undefined;
    const operatore = this.getOperatoreCombobox()?.value ? String(this.getOperatoreCombobox()!.value) : undefined;
    const note = (this.$('[data-field="note"]') as HTMLTextAreaElement | null)?.value || undefined;
    const coloreId = this.getColoreId();
    const modalitaRaccolta = this.getModalitaRaccolta();

    let result;

    if (this.delegaId) {
      result = await this.api.deleghe.aggiorna(this.delegaId, {
        cassa: cassa as 'CE' | 'EC' | undefined,
        dataDecorrenza: dataDecorrenza ?? null,
        operatore: operatore ?? null,
        note: note ?? null,
        ...(coloreId ? { coloreId } : {}),
        ...(modalitaRaccolta ? { modalitaRaccolta } : {}),
      });
    } else {
      if (!this.lavoratore || !cassa || !coloreId || !modalitaRaccolta) return;
      result = await this.api.deleghe.crea({
        lavoratoreId: this.lavoratore.id,
        cassa: cassa as 'CE' | 'EC',
        dataDecorrenza,
        operatore,
        note,
        coloreId,
        modalitaRaccolta,
      });
    }

    if (!result.ok) {
      if (erroreContainer) {
        erroreContainer.innerHTML = `<div class="alert alert-danger alert-sm py-1 px-2">${result.error}</div>`;
      }
      if (confirmBtn) {
        confirmBtn.disabled = false;
        confirmBtn.innerHTML = '<i class="bi bi-check-circle me-1"></i>Conferma';
      }
      return;
    }

    const delega = result.data;

    // Collega disdetta se proveniamo da una disdetta
    if (this.fromDisdettaId > 0 && delega.id) {
      try {
        await this.api.disdette.collegaDelega(this.fromDisdettaId, delega.id);
      } catch {
        // Non bloccare il flusso se il collegamento fallisce
      }
    }

    this.showStep('successo');

    const messaggio = this.messaggioSalvataggio(delega, cassa);
    const msgEl = this.$('[data-messaggio-successo]');
    if (msgEl) {
      msgEl.textContent = messaggio;
    }

    const detail = {
      type: this.delegaId ? 'delega-aggiornata' : 'delega-creata',
      id: delega.id,
    };

    // In contesto modale/iframe: chiudi automaticamente
    // In pagina normale: mostra bottone per inserirne un'altra
    if (window !== window.parent || this.closest('.modal')) {
      // La modale si chiude prima che si legga: la collocazione automatica (altra cassa o Cessati/Trasferiti) resta come toast nella pagina sotto
      const collocata = (cassa && delega.cassa !== cassa) || !!delega.altraCassa?.create || delega.stato === 'BOZZA';
      if (collocata) this.toastNellaPagina(messaggio);
      setTimeout(() => this.notifyAndClose('delega-saved', detail), 1500);
    } else {
      this.dispatchEvent(new CustomEvent('delega-saved', { bubbles: true, composed: true, detail }));
      const nuovaBtn = this.$('[data-action="nuova-delega"]');
      if (nuovaBtn) nuovaBtn.classList.remove('d-none');
    }
  }

  // --- Chiusura modale / notifica al contesto parent ---

  /** Toast nella finestra che contiene l'iframe (stessa origine), altrimenti in quella corrente */
  private toastNellaPagina(messaggio: string): void {
    const weext = (window.parent as typeof window & { Weext?: typeof Weext }).Weext ?? Weext;
    weext.toast(messaggio, { type: 'info', autohide: false });
  }

  // --- Salvataggio in archivio (sospesa) ---

  private async submitArchivio(): Promise<void> {
    const combo = this.getOperatoreCombobox();
    if (!combo?.value) {
      await (globalThis as any).Weext.alert('Selezionare un operatore');
      return;
    }
    if (!this.lavoratore?.codiceFiscale) {
      await (globalThis as any).Weext.alert('Lavoratore senza codice fiscale: impossibile salvare in archivio');
      return;
    }
    if (!this.verificaColoreModalita()) return;
    const coloreId = this.getColoreId()!;
    const modalitaRaccolta = this.getModalitaRaccolta()!;

    const btn = this.$('[data-action="vai-riepilogo"]') as HTMLButtonElement | null;
    if (btn) { btn.disabled = true; btn.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Salvataggio...'; }

    const noteEl = this.$('[data-field="note"]') as HTMLTextAreaElement | null;
    const result = await this.api.archivio.crea({
      codiceFiscale: this.lavoratore.codiceFiscale,
      operatore: String(combo.value),
      note: noteEl?.value?.trim() || undefined,
      coloreId,
      modalitaRaccolta,
    });

    if (!result.ok) {
      if (btn) { btn.disabled = false; btn.textContent = 'Salva in archivio'; }
      const errEl = this.$('[data-errore]');
      if (errEl) { errEl.textContent = result.error; errEl.classList.remove('d-none'); }
      return;
    }

    // Successo: stessa logica del submit normale
    this.showStep('successo');
    const msgEl = this.$('[data-messaggio-successo]');
    if (msgEl) msgEl.textContent = 'Delega salvata in archivio — sarà incrociata al prossimo import cassa';

    const detail = { type: 'archivio-creato', id: result.data.id };

    if (window !== window.parent || this.closest('.modal')) {
      setTimeout(() => this.notifyAndClose('delega-saved', detail), 1500);
    } else {
      this.dispatchEvent(new CustomEvent('delega-saved', { bubbles: true, composed: true, detail }));
      const nuovaBtn = this.$('[data-action="nuova-delega"]');
      if (nuovaBtn) nuovaBtn.classList.remove('d-none');
    }
  }

  // --- Utility: radio cassa ---

  private getCassa(): string {
    const checked = this.$('[data-field="cassa"] input[name="cassa"]:checked') as HTMLInputElement | null;
    return checked?.value ?? '';
  }

  private setCassa(value: string): void {
    const radio = this.$(`[data-field="cassa"] input[value="${value}"]`) as HTMLInputElement | null;
    if (radio) radio.checked = true;
  }

  // --- Utility: colore e modalità di raccolta ---

  private get colori(): ColoreDelega[] {
    return (this.buildRenderingData().colori as ColoreDelega[] | undefined) ?? [];
  }

  private getColoreId(): number | null {
    const checked = this.$('[data-field="colore"] input[name="colore"]:checked') as HTMLInputElement | null;
    return checked ? Number(checked.value) : null;
  }

  private getModalitaRaccolta(): ModalitaRaccolta | null {
    const select = this.$('[data-field="modalitaRaccolta"]') as HTMLSelectElement | null;
    return (select?.value || null) as ModalitaRaccolta | null;
  }

  /** In modifica: il colore attuale resta selezionato e visibile anche se non più valido */
  private setColoreModalita(coloreId: number | null, modalita: string | null): void {
    const radio = this.$(`[data-field="colore"] input[value="${coloreId}"]`) as HTMLInputElement | null;
    if (radio) {
      radio.checked = true;
      radio.closest('[data-colore-non-valido]')?.classList.remove('d-none');
    }
    const select = this.$('[data-field="modalitaRaccolta"]') as HTMLSelectElement | null;
    if (select && modalita) select.value = modalita;
  }

  /** Colore e modalità obbligatori nei nuovi inserimenti (delega e archivio) */
  private verificaColoreModalita(): boolean {
    if (!this.getColoreId()) {
      Weext.toast('Seleziona il colore della delega', { type: 'warning' });
      return false;
    }
    if (!this.getModalitaRaccolta()) {
      Weext.toast('Seleziona la modalità di raccolta', { type: 'warning' });
      return false;
    }
    return true;
  }

  /** Esito del salvataggio: cassa e stato finali, più l'eventuale collocazione automatica */
  private messaggioSalvataggio(
    delega: { id: number; cassa: string; stato?: string; altraCassa?: { create: number } },
    cassaScelta?: string,
  ): string {
    const azione = this.delegaId ? 'aggiornata' : 'inserita';
    const parti = [`Delega ${azione} (ID: ${delega.id}) — ${delega.cassa}, stato: ${etichettaStato(delega.stato ?? '')}.`];
    if (cassaScelta && delega.cassa !== cassaScelta) {
      parti.push(`Collocata in ${delega.cassa}: il lavoratore è attivo lì.`);
    }
    if (delega.altraCassa?.create) {
      const altra = delega.cassa === 'CE' ? 'EC' : 'CE';
      parti.push(`Proposta automatica anche per ${altra}: il lavoratore è attivo e senza sindacato in entrambe.`);
    }
    return parti.join(' ');
  }

  // --- Utility: mostra solo lo step indicato ---

  private showStep(step: number | string): void {
    this.step = typeof step === 'number' ? step : 0;
    for (const el of this.$$('[data-step]')) {
      el.classList.toggle('d-none', el.dataset.step !== String(step));
    }
  }

  // --- Modale Nuovo Lavoratore (usa wc-edit-lavoratore) ---

  private appendModals(): void {
    for (const el of this.$$('[data-modal]')) {
      document.body.appendChild(el);
    }
  }

  private getModal(selector: string): ModalInstance | null {
    const el = document.querySelector(selector);
    if (!el) return null;
    const bs = (globalThis as any).bootstrap;
    const existing = bs?.Modal?.getInstance(el);
    if (existing) return existing;
    const m = new bs.Modal(el);
    this.modals.push(m);
    return m;
  }

  private setupNuovoLavoratore(): void {
    this.delegate.on('click', '[data-action="nuovo-lavoratore"]', () => {
      const comp = document.querySelector('#modalNuovoLavoratore wc-edit-lavoratore') as HTMLElement & { reset(): void } | null;
      if (comp) comp.reset();
      this.getModal('#modalNuovoLavoratore')?.show();
    });

    this.listen(document, 'lavoratore-salvato', ((e: CustomEvent) => {
      this.getModal('#modalNuovoLavoratore')?.hide();
      this.lavoratore = e.detail.lavoratore;
      this.goToStep2();
    }) as EventListener);
  }

  protected override onDisconnected(): void {
    for (const m of this.modals) {
      try { m.dispose(); } catch { /* ignore */ }
    }
    this.modals = [];
    const modale = document.querySelector('[data-modal="nuovo-lavoratore"]');
    if (modale) modale.remove();
  }
}
